import React from 'react';
import PropTypes from 'prop-types';
import sum from 'lodash/sum';

import PageSection from '../components/PageSection';
import AttributesGraphs from '../components/AttributesGraphs';
import VisitHQ from '../components/VisitHQ';
import useCarousel from '../hooks/carousel';
import { useContentfulEntries } from '../hooks/contentful';
import useAttributesComparison from '../hooks/attributes-comparison';
import { contentfulConfigType } from '../proptypes/contentful';
import rankingType, { additionalRankingType } from '../proptypes/ranking';
import phrasesType from '../proptypes/phrases';
import comparisonOptionsType from '../proptypes/comparison-options';
import attributesConfigType from '../proptypes/attributes-config';
import { ssColorBlack, ssColorDarkNavy } from '../constants/style-constants';

const hasValues = ranking => (
  sum(Object.values(ranking)) > 0
);

const extractGlobalRankingsFromContentful = ({ items }) => {
  if (!items) return {};

  return items.reduce((memo, { sys: { id }, fields }) => ({
    ...memo,
    [id]: {
      name: fields.name,
      spyRole: fields.spyRole,
      ranking: {
        conscientiousness: fields.conscientiousness,
        inquisitiveness: fields.inquisitiveness,
        coldRisk: fields.coldRisk,
        hotRisk: fields.hotRisk,
        composure: fields.composure,
        mentalHorsepower: fields.mentalHorsepower,
        sociability: fields.sociability,
        agility: fields.agility,
        interpersonalSkills: fields.interpersonalSkills,
        observation: fields.observation,
      },
    },
  }), {});
};

const ArchetypeRankingsSection = ({
  ranking,
  additionalRanking,
  averageRanking,
  phrases: { rankingTitle: title, ...phrases },
  config,
  attributePath,
  comparisonOptions: groupComparisonOptions,
  contentfulConfig,
}) => {
  const globalRankings = extractGlobalRankingsFromContentful(useContentfulEntries(contentfulConfig, 'attributeComparison'));
  const globalComparisons = Object.entries(globalRankings).map(
    ([id, { name, spyRole }]) => ({ id, name, spyRole }),
  );

  const comparisonOptions = [
    { id: 'average', name: phrases.averageRankingChartLayerName, spyRole: phrases.averageRankingChartLayerSubTitle },
    ...groupComparisonOptions,
    ...globalComparisons,
  ];

  const [comparison, rotateComparison, comparisonOptionIndex] = useCarousel(comparisonOptions);

  const {
    layers,
    attributes,
    additionalAttributes,
  } = useAttributesComparison({
    ranking,
    additionalRanking,
    averageRanking,
    comparison,
    config,
    phrases,
    attributePath,
    globalRankings,
  });

  if (layers === undefined) return null;
  const showMuseumAttributes = hasValues(additionalRanking);

  return (
    <>
      <PageSection theme="dark_navy">
        <AttributesGraphs
          className="coreAttributes"
          title={phrases.coreAttributesTitle}
          layers={layers}
          data={attributes}
          style={{ background: ssColorBlack }}
          phrases={phrases}
          comparison={comparison}
          rotateComparison={rotateComparison}
          carousel={{
            selected: comparisonOptionIndex,
            total: comparisonOptions.length,
          }}
        />
      </PageSection>
      <PageSection theme="black">
        {
          showMuseumAttributes ? (
            <AttributesGraphs
              className="additionalAttributes"
              title={phrases.additionalAttributesTitle}
              layers={layers}
              data={additionalAttributes}
              style={{ background: ssColorDarkNavy }}
              phrases={phrases}
              comparison={comparison}
              rotateComparison={rotateComparison}
              carousel={{
                selected: comparisonOptionIndex,
                total: comparisonOptions.length,
              }}
            />
          ) : (
            <VisitHQ phrases={phrases.visitHq} />
          )
        }
      </PageSection>
    </>
  );
};

ArchetypeRankingsSection.defaultProps = {
  attributePath: null,
};

ArchetypeRankingsSection.propTypes = {
  ranking: rankingType.isRequired,
  additionalRanking: additionalRankingType.isRequired,
  averageRanking: rankingType.isRequired,
  phrases: phrasesType.isRequired,
  config: attributesConfigType.isRequired,
  attributePath: PropTypes.string,
  comparisonOptions: comparisonOptionsType.isRequired,
  contentfulConfig: contentfulConfigType.isRequired,
};

export default ArchetypeRankingsSection;
